import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';

const InviteAcceptPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [invite, setInvite] = useState(null);
    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) {
            setError("招待トークンが必要です");
            setLoading(false);
            return;
        }

        const fetchInvite = async () => {
            try {
                // Find invite by token
                const invitesRef = collection(db, 'team_invites');
                const q = query(invitesRef, where('token', '==', token));
                const snapshot = await getDocs(q);

                if (snapshot.empty) {
                    setError('招待が見つかりません');
                    return;
                }

                const inviteDoc = snapshot.docs[0];
                const inviteData = { id: inviteDoc.id, ...inviteDoc.data() };

                // Expiry check
                // Note: Firestore Timestamp handling. If expiresAt is Timestamp, convert.
                const expiresAt = inviteData.expiresAt.toDate ? inviteData.expiresAt.toDate() : new Date(inviteData.expiresAt);

                if (expiresAt < new Date()) {
                    setError('この招待は期限切れです');
                    return;
                }

                if (inviteData.status !== 'pending') {
                    setError('この招待は既に使用されています');
                    return;
                }

                setInvite(inviteData);

                // Fetch Team Info
                const teamDocSnap = await getDoc(doc(db, 'teams', inviteData.teamId));
                if (teamDocSnap.exists()) {
                    setTeam({ id: teamDocSnap.id, ...teamDocSnap.data() });
                } else {
                    setError('チームが見つかりません');
                }

            } catch (err) {
                console.error("Fetch invite error:", err);
                setError('エラーが発生しました');
            } finally {
                setLoading(false);
            }
        };

        fetchInvite();
    }, [token]);

    const handleAccept = async () => {
        if (!user) {
            // Redirect to login with return url
            // Assuming /login handles 'redirect' query param or we use a more complex auth flow.
            // For now, simpler: user clicks login, loops back or manual navigate.
            navigate(`/login?redirect=/invite/${token}`);
            return;
        }

        try {
            setLoading(true);

            // 1. Add user to group members（新データ構造対応）
            const targetGroupId = invite.groupId;
            if (!targetGroupId) throw new Error('グループ情報が見つかりません');
            await addDoc(collection(db, `teams/${invite.teamId}/groups/${targetGroupId}/members`), {
                userId: user.uid,
                role: invite.role || 'member',
                joinedAt: serverTimestamp(),
                displayName: user.displayName || '',
                email: user.email || '',
                invitedBy: invite.inviterName || 'unknown'
            });

            // 2. Update invite status
            await updateDoc(doc(db, 'team_invites', invite.id), {
                status: 'accepted'
            });

            // 3. Navigate to dashboard/team
            // Assuming the dashboard supports viewing the team.
            navigate('/dashboard?tab=team');

        } catch (err) {
            console.error("Accept invite error:", err);
            setError('参加に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className="text-gray-600">読み込み中...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-md text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">⚠️</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">エラー</h2>
                <p className="text-red-500 mb-6">{error}</p>
                <button
                    onClick={() => navigate('/')}
                    className="text-indigo-600 hover:text-indigo-800 font-medium"
                >
                    トップページへ戻る
                </button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
            <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full text-center border border-slate-700 shadow-xl">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-white mb-2">チームへの招待</h1>
                    <p className="text-slate-400 text-sm">You have been invited to join a team.</p>
                </div>

                <div className="bg-slate-700/50 rounded-xl p-6 mb-8">
                    <h2 className="text-xl font-bold text-indigo-400 mb-2">{team?.name}</h2>
                    <p className="text-slate-300">
                        <span className="font-semibold text-white">{invite?.inviterName}</span> さんから招待が届いています。
                    </p>
                </div>

                <button
                    onClick={handleAccept}
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 rounded-xl font-bold text-lg hover:from-indigo-400 hover:to-purple-400 transition-all shadow-lg shadow-indigo-500/25"
                >
                    チームに参加する
                </button>

                {!user && (
                    <p className="mt-4 text-xs text-slate-500">
                        ※ 参加するにはログインが必要です
                    </p>
                )}
            </div>
        </div>
    );
};

export default InviteAcceptPage;
